import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress, WarningPanel } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { useVaultRequest } from '../../hooks/useVaultRequest';
import { useLiveUpdates } from '../../hooks/useLiveUpdates';
import { stateLabel } from '../../lib/requestStateLabels';

const STATE_DOT_COLOR: Record<string, string> = {
  pending_approval: '#2196f3',
  approved: '#4caf50',
  pr_open: '#ff9800',
  deploying: '#1976d2',
  deployed: '#4caf50',
  rejected_approval: '#f44336',
  deploy_failed: '#f44336',
  stuck: '#ff5722',
  abandoned: '#9e9e9e',
  cleanup_failed: '#f44336',
  gate2_denied: '#f44336',
  // snow_tech_lead_approved/snow_group_approved/snow_platform_approved/
  // snow_manager_approved/snow_ar_pending/snow_exception_review
  // deliberately absent — per GH #598, ServiceNow only ever pushes the
  // terminal approved/rejected decision; these intermediate milestones can
  // never actually be driven by a real SNOW signal.
  snow_stalled: '#d84315',
  gitops_stalled: '#6a1b9a',
};

const useStyles = makeStyles(theme => ({
  timeline: {
    position: 'relative',
    paddingLeft: 24,
    paddingTop: 8,
    paddingBottom: 8,
  },
  connector: {
    position: 'absolute',
    left: 8,
    top: 12,
    bottom: 12,
    width: 2,
    background: theme.palette.divider,
  },
  event: { position: 'relative', marginBottom: 24 },
  dot: {
    position: 'absolute',
    left: -24,
    top: 3,
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: '2px solid white',
  },
  stateName: { fontWeight: 600, fontSize: 13 },
  meta: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 2 },
  note: {
    fontSize: 11,
    marginTop: 4,
    fontFamily: 'monospace',
    background: '#f5f5f5',
    padding: '4px 8px',
    borderRadius: 4,
  },
  timestamp: {
    float: 'right',
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
}));

export function EntityVaultRequestTrailCard() {
  const { entity } = useEntity();
  const requestId = entity.metadata.annotations?.['vault.ice.com/request-id'];
  const { events, loading, error, reload } = useVaultRequest(requestId);
  useLiveUpdates(requestId ? `vault:request:${requestId}` : undefined, reload);
  const classes = useStyles();

  if (loading) return <Progress />;
  if (error)
    return (
      <WarningPanel
        title="Failed to load state trail"
        message={error.message}
      />
    );

  const sorted = [...events].sort(
    (a, b) =>
      new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
  );

  return (
    <InfoCard title="State Trail" subheader="Most recent at top">
      <div className={classes.timeline}>
        <div className={classes.connector} />
        {sorted.map(evt => (
          <div key={evt.id} className={classes.event}>
            <div
              className={classes.dot}
              style={{
                background: STATE_DOT_COLOR[evt.toState] ?? '#9e9e9e',
                boxShadow: `0 0 0 2px ${STATE_DOT_COLOR[evt.toState] ?? '#9e9e9e'}`,
              }}
            />
            <span className={classes.timestamp}>
              {new Date(evt.occurredAt)
                .toISOString()
                .slice(0, 16)
                .replace('T', ' ')}
            </span>
            <Typography className={classes.stateName}>
              {stateLabel(evt.toState)}
            </Typography>
            <Typography className={classes.meta}>actor: {evt.actor}</Typography>
            {evt.chgNumber && (
              <Typography className={classes.meta}>
                CHG: {evt.chgNumber}
              </Typography>
            )}
            {evt.note && <div className={classes.note}>{evt.note}</div>}
          </div>
        ))}
        {events.length === 0 && (
          <Typography color="textSecondary">No events yet.</Typography>
        )}
      </div>
    </InfoCard>
  );
}
